/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAuthSearchFilters } from 'app/um/services/auth-service';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { fireEvent } from 'app/shared/lib/fire-events';
import { guid } from 'app/shared/lib/guid';
import { countProviderFilters, IProviderSearchFilters } from 'app/cd/services/provider-service';
import { countAuthFilters } from 'app/um/services/auth-service';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import { ProviderSearchFilters } from 'app/cd/elements/providers/provider-search-filters';
import { deepClone, isNullOrUndefined } from 'app/shared/lib/utilities';
import { debounce } from 'app/shared/lib/decorators';

interface IAuthFilterCustomEvent {
    authSearchFilters: IAuthSearchFilters,
    providerSearchFilters: IProviderSearchFilters
}

@autoinject()
export class FilterAuths extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected authSearchFilters: IAuthSearchFilters = {};
    protected tempAuthSearchFilters: IAuthSearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected providerSearchFilters: IProviderSearchFilters = {};
    protected tempProviderSearchFilters: IProviderSearchFilters = {};

    @bindable.objectNull()
    protected authAvailableFilters: string[] | null = null;

    @bindable.objectNull()
    protected providerAvailableFilters: string[] | null = null;

    protected id = guid();

    ////****************** Element References ***************************************////
    private $collapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;
    protected $providerSearchFilters!: ProviderSearchFilters;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////
    protected handleSubmit(): void {
        if(!this.state.saving && !this.state.loading) this.search();
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }

    ////****************** Functions ************************************************////
    protected showAvailableCriteria(): void {
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    protected showSelectedCriteria(): void {
        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    protected collapseBoth(): void {
        this.$collapsible.close(0);
        this.$collapsible.close(1);
    }

    protected search(): void {
        if(isNullOrUndefined(this.tempAuthSearchFilters.dueDateEnd)) {
            this.tempAuthSearchFilters.dueDateEnd = this.tempAuthSearchFilters.dueDateStart;
        }

        if(isNullOrUndefined(this.tempAuthSearchFilters.requestedDateEnd)) {
            this.tempAuthSearchFilters.requestedDateEnd = this.tempAuthSearchFilters.requestedDateStart;
        }

        if(isNullOrUndefined(this.tempAuthSearchFilters.nextReviewDateEnd)) {
            this.tempAuthSearchFilters.nextReviewDateEnd = this.tempAuthSearchFilters.nextReviewDateStart;
        }

        this.authSearchFilters = deepClone(this.tempAuthSearchFilters);
        this.providerSearchFilters = deepClone(this.tempProviderSearchFilters);
        this.fireSearch();
        const authFilterCount = countAuthFilters(this.authSearchFilters);
        const providerFilterCount = countProviderFilters(this.providerSearchFilters);
        if(authFilterCount + providerFilterCount === 0) {
            this.$collapsible.close(0);
        } else {
            this.showSelectedCriteria();
        }
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    // debounce gives the bindings time to reach the parent component
    @debounce(0)
    protected fireSearch(): void {
        const detail: IAuthFilterCustomEvent = {
            authSearchFilters: this.authSearchFilters,
            providerSearchFilters: this.providerSearchFilters
        };
        fireEvent(this.$element, 'search', detail);
    }

    protected clear(returnFocus?: boolean, andCollapse?: boolean): void {
        this.authSearchFilters = {};
        this.tempAuthSearchFilters = {};
        this.providerSearchFilters = {};
        this.tempProviderSearchFilters = {};
        this.$providerSearchFilters.clearSpecialty();

        if(andCollapse) this.collapseBoth();
        else this.showAvailableCriteria();

        fireEvent(this.$element, 'clear-results');

        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }
}
